// src/CqcSites.js
// Entry point for the CQC module. Shows onboarding when the organisation has
// no sites yet; otherwise an overview grid (one card per site: CQC rating,
// overall compliance %, last sync date, resync button) that links into the
// full drill-down (CqcCompliance.js) for a chosen site or "All sites".
//
// This is the component to mount in App.js:
//   {currentView === "cqc_compliance" && <CqcSites token={token} userData={userData} />}

import React, { useEffect, useMemo, useState } from "react";
import CqcSiteOnboarding from "./CqcSiteOnboarding";
import CqcCompliance from "./CqcCompliance";
import { cqcApi } from "./cqcApi";
import { GROUPS, deriveSector } from "./cqcTaxonomy";
import { pctBg, fmtPct, RatingBadge } from "./cqcColors";
import { attestationIndex, applicabilityIndex, overallPctForSite } from "./cqcRollup";

const S = {
  wrap: { fontFamily: "system-ui, sans-serif", padding: "16px 4px" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 },
  h1: { fontSize: 20, fontWeight: 700, margin: 0 },
  sub: { fontSize: 12.5, color: "#6b7280", marginBottom: 16 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 },
  card: { border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fff", cursor: "pointer" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 },
  name: { fontWeight: 700, fontSize: 14.5 },
  addr: { fontSize: 12, color: "#6b7280", marginBottom: 8 },
  pctBar: { height: 8, borderRadius: 999, background: "#f1f5f9", overflow: "hidden", marginBottom: 6 },
  pctFill: (pct) => ({ height: "100%", width: `${pct || 0}%`, background: pctBg(pct) }),
  meta: { fontSize: 11, color: "#9ca3af", display: "flex", justifyContent: "space-between" },
  addBtn: { padding: "8px 14px", fontSize: 13, fontWeight: 600, border: "1px solid #d1d5db", borderRadius: 6,
            background: "#fff", cursor: "pointer" },
  allCard: { border: "1px dashed #cbd5e1", borderRadius: 10, padding: 14, display: "flex",
             alignItems: "center", justifyContent: "center", color: "#2563eb", fontWeight: 600,
             fontSize: 13.5, cursor: "pointer", minHeight: 88 },
  backBtn: { fontSize: 12.5, color: "#2563eb", background: "none", border: "none", cursor: "pointer", marginBottom: 10 },
};

export default function CqcSites() {
  const [sites, setSites] = useState(null); // null = loading
  const [attestations, setAttestations] = useState([]);
  const [applicability, setApplicability] = useState([]);
  const [error, setError] = useState(null);
  const [view, setView] = useState({ mode: "grid" }); // grid | onboarding | drilldown

  async function load() {
    try {
      const [sitesRes, attRes, appRes] = await Promise.all([
        cqcApi.listSites(), cqcApi.listAttestations(), cqcApi.listApplicability(),
      ]);
      setSites(sitesRes.sites || []);
      setAttestations(attRes.attestations || []);
      setApplicability(appRes.applicability || []);
    } catch (e) {
      setError(e.message);
    }
  }
  useEffect(() => { load(); }, []);

  const attByKey = useMemo(() => attestationIndex(attestations), [attestations]);
  const appByKey = useMemo(() => applicabilityIndex(applicability), [applicability]);

  if (error) return <div style={{ ...S.wrap, color: "#991b1b" }}>Couldn't load sites: {error}</div>;
  if (sites === null) return <div style={S.wrap}>Loading…</div>;

  if (view.mode === "onboarding" || sites.length === 0) {
    return (
      <div style={S.wrap}>
        {sites.length > 0 && <button style={S.backBtn} onClick={() => setView({ mode: "grid" })}>← Back to sites</button>}
        <CqcSiteOnboarding onImported={() => { load(); setView({ mode: "grid" }); }} />
      </div>
    );
  }

  if (view.mode === "drilldown") {
    return (
      <div style={S.wrap}>
        <button style={S.backBtn} onClick={() => setView({ mode: "grid" })}>← Back to sites</button>
        <CqcCompliance initialSiteId={view.siteId || null} />
      </div>
    );
  }

  async function resync(siteId, e) {
    e.stopPropagation();
    try { await cqcApi.resyncSite(siteId); await load(); }
    catch (e2) { setError(e2.message); }
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h1 style={S.h1}>CQC Compliance</h1>
        <button style={S.addBtn} onClick={() => setView({ mode: "onboarding" })}>+ Add sites</button>
      </div>
      <div style={S.sub}>
        {sites.length} site{sites.length === 1 ? "" : "s"} tracked against CQC's single assessment framework
        (5 key questions, 34 quality statements).
      </div>

      <div style={S.grid}>
        <div style={S.allCard} onClick={() => setView({ mode: "drilldown", siteId: null })}>
          View all sites →
        </div>
        {sites.map((s) => {
          const pct = overallPctForSite(attByKey, s._id, GROUPS, appByKey);
          const sector = deriveSector(s);
          return (
            <div key={s._id} style={S.card} onClick={() => setView({ mode: "drilldown", siteId: s._id })}>
              <div style={S.cardTop}>
                <div>
                  <div style={S.name}>{s.name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{sector.label}</div>
                </div>
                {s.ratings && <RatingBadge rating={s.ratings.overall} />}
              </div>
              <div style={S.addr}>{s.postcode}</div>
              <div style={S.pctBar}><div style={S.pctFill(pct)} /></div>
              <div style={S.meta}>
                <span>{fmtPct(pct)} evidenced</span>
                <span>
                  {s.cqcSyncedAt ? `Synced ${new Date(s.cqcSyncedAt).toLocaleDateString()}` : "Not synced"}
                  {" · "}
                  <button style={{ ...S.addBtn, padding: "2px 8px", fontSize: 11 }} onClick={(e) => resync(s._id, e)}>Resync</button>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
